/**
 * The modal's "Create a target" and "Set an alert" panels.
 * - Target: One-time (condition, value, start date defaulting to today,
 *   optional due date, description) or Recurring (repeats daily … annually,
 *   condition, value, description). Suggest Target fills in last month's value.
 * - Alert: condition, value, interval (Day … Annual), description. Alerts
 *   arrive in the inbox only, as in AgencyAnalytics.
 */

import { useState, type ReactNode } from 'react';

import type { MetricFormat } from '../reports/reports-api.ts';
import {
  ALERT_CONDITIONS,
  ALERT_INTERVALS,
  TARGET_CONDITIONS,
  TARGET_PERIODS,
  unitHint,
} from './kpi-format.ts';
import type { AlertCondition, AlertInterval, TargetCondition, TargetPeriod } from './kpis-api.ts';

export interface TargetForm {
  enabled: boolean;
  type: 'ONE_TIME' | 'RECURRING';
  period: TargetPeriod;
  condition: TargetCondition | '';
  value: string;
  start: string;
  due: string;
  description: string;
}

export interface AlertForm {
  enabled: boolean;
  condition: AlertCondition | '';
  value: string;
  interval: AlertInterval;
  description: string;
}

function Options<T extends string>({ options }: { options: [T, string][] }): ReactNode {
  return options.map(([value, label]) => (
    <option key={value} value={value}>
      {label}
    </option>
  ));
}

export function TargetPanel(props: {
  form: TargetForm;
  format: MetricFormat;
  onChange: (form: TargetForm) => void;
  /** Resolves to the typed value of last month, or a reason it could not. */
  onSuggest: () => Promise<string | null>;
}): ReactNode {
  const { form, onChange } = props;
  const [note, setNote] = useState<string | null>(null);
  const set = (patch: Partial<TargetForm>) => onChange({ ...form, ...patch });
  const suggest = () => {
    setNote('Fetching last month…');
    props
      .onSuggest()
      .then(setNote, (error: unknown) =>
        setNote(error instanceof Error ? error.message : 'No suggestion available.'),
      );
  };
  return (
    <section className="kp-panel" aria-labelledby="kp-target-title">
      <h3 id="kp-target-title">
        <label className="kp-check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => set({ enabled: event.target.checked })}
          />
          Create a target
        </label>
      </h3>
      {form.enabled && (
        <>
          <div className="rb-seg" role="radiogroup" aria-label="Target type">
            {(['ONE_TIME', 'RECURRING'] as const).map((type) => (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={form.type === type}
                className={form.type === type ? 'on' : ''}
                onClick={() => set({ type })}
              >
                {type === 'ONE_TIME' ? 'One-time' : 'Recurring'}
              </button>
            ))}
          </div>
          {form.type === 'RECURRING' && (
            <label className="rs-field">
              Time Period
              <select
                value={form.period}
                onChange={(event) => set({ period: event.target.value as TargetPeriod })}
              >
                <Options options={TARGET_PERIODS} />
              </select>
            </label>
          )}
          <label className="rs-field">
            Condition
            <select
              value={form.condition}
              onChange={(event) => set({ condition: event.target.value as TargetCondition })}
            >
              <option value="">Choose a condition</option>
              <Options options={TARGET_CONDITIONS} />
            </select>
          </label>
          <label className="rs-field">
            Target Value{' '}
            {unitHint(props.format, form.condition) && (
              <small>({unitHint(props.format, form.condition)})</small>
            )}
            <span className="kp-value-row">
              <input
                type="number"
                inputMode="decimal"
                value={form.value}
                onChange={(event) => set({ value: event.target.value })}
              />
              <button
                type="button"
                className="btn secondary"
                title="Fetches data from the last month"
                disabled={form.condition === 'INCREASE_BY' || form.condition === 'DECREASE_BY'}
                onClick={suggest}
              >
                Suggest Target
              </button>
            </span>
          </label>
          {note && (
            <small className="kp-faint" role="status">
              {note}
            </small>
          )}
          {form.type === 'ONE_TIME' && (
            <div className="kp-two">
              <label className="rs-field">
                Start Date
                <input
                  type="date"
                  value={form.start}
                  onChange={(event) => set({ start: event.target.value })}
                />
              </label>
              <label className="rs-field">
                Due Date <small>(optional)</small>
                <input
                  type="date"
                  value={form.due}
                  min={form.start}
                  onChange={(event) => set({ due: event.target.value })}
                />
              </label>
            </div>
          )}
          <label className="rs-field">
            Description <small>(optional)</small>
            <textarea
              rows={2}
              maxLength={500}
              value={form.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </label>
        </>
      )}
    </section>
  );
}

export function AlertPanel(props: {
  form: AlertForm;
  format: MetricFormat;
  onChange: (form: AlertForm) => void;
}): ReactNode {
  const { form, onChange } = props;
  const set = (patch: Partial<AlertForm>) => onChange({ ...form, ...patch });
  return (
    <section className="kp-panel" aria-labelledby="kp-alert-title">
      <h3 id="kp-alert-title">
        <label className="kp-check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => set({ enabled: event.target.checked })}
          />
          Set an alert
        </label>
      </h3>
      {form.enabled && (
        <>
          <label className="rs-field">
            Condition
            <select
              value={form.condition}
              onChange={(event) => set({ condition: event.target.value as AlertCondition })}
            >
              <option value="">Choose a condition</option>
              <Options options={ALERT_CONDITIONS} />
            </select>
          </label>
          <label className="rs-field">
            Target{' '}
            {unitHint(props.format, form.condition) && (
              <small>({unitHint(props.format, form.condition)})</small>
            )}
            <input
              type="number"
              inputMode="decimal"
              value={form.value}
              onChange={(event) => set({ value: event.target.value })}
            />
          </label>
          <label className="rs-field">
            Interval
            <select
              value={form.interval}
              onChange={(event) => set({ interval: event.target.value as AlertInterval })}
            >
              <Options options={ALERT_INTERVALS} />
            </select>
          </label>
          <label className="rs-field">
            Description <small>(optional)</small>
            <textarea
              rows={2}
              maxLength={500}
              value={form.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </label>
          <small className="kp-faint">
            Checked when each interval ends. Alerts arrive in your inbox.
          </small>
        </>
      )}
    </section>
  );
}
